import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { ADMIN_BASE } from '../../utils/apiBase';
import { resolveImageUrl } from '../../utils/imageUrl';

const API = ADMIN_BASE;
const MAX_IMAGE_UPLOAD_BYTES = 4 * 1024 * 1024; // 4MB final payload cap
const MAX_IMAGE_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Invalid image file.'));
    };
    img.src = objectUrl;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Could not process image.'));
        return;
      }
      resolve(blob);
    }, type, quality);
  });

const optimizeImageForUpload = async (file) => {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please upload a valid image file.');
  }

  const img = await loadImage(file);
  const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not process image.');
  }

  ctx.drawImage(img, 0, 0, width, height);

  let optimized = await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY);
  if (optimized.size >= file.size) {
    optimized = file;
  }

  if (optimized.size > MAX_IMAGE_UPLOAD_BYTES) {
    throw new Error('Image is too large. Please use an image smaller than 4MB.');
  }

  if (optimized instanceof File) {
    return optimized;
  }

  const safeName = (file.name || 'image').replace(/\.[^.]+$/, '');
  return new File([optimized], `${safeName}.jpg`, { type: 'image/jpeg' });
};

const AdminProductOperation = () => {
  const { getAuthHeader } = useAdmin();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('id');
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'hair',
    description: '',
    key_benefits: [''],
    seo_title: '',
    price: '',
    status: 'active',
    image_url: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingProduct, setFetchingProduct] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');
  const [previewError, setPreviewError] = useState(false);

  const isEditMode = !!productId;

  useEffect(() => {
    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  const fetchProduct = async () => {
    setFetchingProduct(true);
    try {
      const res = await axios.get(`${API}/products`, {
        headers: getAuthHeader()
      });
      const product = res.data.find(p => p.id === productId);
      
      if (product) {
        setFormData({
          name: product.name,
          category: product.category,
          description: product.description,
          key_benefits: product.key_benefits?.length ? product.key_benefits : [''],
          seo_title: product.seo_title,
          price: product.price.toString(),
          status: product.status || 'active',
          image_url: product.image_url || ''
        });
      } else {
        toast.error('Product not found');
        navigate('/admin/products');
      }
    } catch (error) {
      toast.error('Failed to fetch product');
    } finally {
      setFetchingProduct(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    setLocalPreviewUrl(objectUrl);
    setPreviewError(false);
    setUploadingImage(true);

    try {
      const optimizedFile = await optimizeImageForUpload(file);
      const formData = new FormData();
      formData.append('file', optimizedFile);

      const res = await axios.post(`${API}/upload-image`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      const uploadedUrl = res.data.public_url || res.data.image_url;
      setFormData(prev => ({ ...prev, image_url: uploadedUrl }));
      toast.success('Image uploaded!');
    } catch (error) {
      const message = error?.response?.data?.detail || error?.message || 'Image upload failed';
      toast.error(message);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    return () => {
      if (localPreviewUrl && localPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const productData = {
      ...formData,
      price: parseFloat(formData.price),
      key_benefits: formData.key_benefits.filter(b => b.trim())
    };

    try {
      if (isEditMode) {
        // UPDATE existing product
        await axios.put(
          `${API}/products/${productId}`,
          productData,
          { headers: getAuthHeader() }
        );
        toast.success('Product updated successfully!');
      } else {
        // CREATE new product
        await axios.post(
          `${API}/products`,
          productData,
          { headers: getAuthHeader() }
        );
        toast.success('Product created successfully!');
      }
      
      navigate('/admin/products');
    } catch (error) {
      console.error('Operation failed:', error);
      toast.error(isEditMode ? 'Update failed' : 'Create failed');
    } finally {
      setLoading(false);
    }
  };

  const addBenefit = () => {
    setFormData(prev => ({ ...prev, key_benefits: [...prev.key_benefits, ''] }));
  };

  const updateBenefit = (index, value) => {
    const newBenefits = [...formData.key_benefits];
    newBenefits[index] = value;
    setFormData(prev => ({ ...prev, key_benefits: newBenefits }));
  };

  const removeBenefit = (index) => {
    if (formData.key_benefits.length > 1) {
      const newBenefits = formData.key_benefits.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, key_benefits: newBenefits }));
    }
  };

  if (fetchingProduct) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading product...</div>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => navigate('/admin/products')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6"
      >
        <ArrowLeft size={20} />
        Back to Products
      </button>

      <h1 className="text-3xl font-bold text-gray-800 mb-6">
        {isEditMode ? 'Edit Product' : 'Create Product'}
      </h1>

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                placeholder="Enter product name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
              >
                <option value="hair">Hair Care</option>
                <option value="skin">Skin Care</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Price (₹) *
              </label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
                min="0"
                step="0.01"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                placeholder="Enter price"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
              >
                <option value="active">Active (Visible on Website)</option>
                <option value="inactive">Inactive (Hidden from Website)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows="4"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
              placeholder="Enter product description"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              SEO Title *
            </label>
            <input
              type="text"
              value={formData.seo_title}
              onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
              placeholder="Enter SEO title"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Key Benefits
            </label>
            {formData.key_benefits.map((benefit, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={benefit}
                  onChange={(e) => updateBenefit(index, e.target.value)}
                  placeholder={`Benefit ${index + 1}`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                />
                {formData.key_benefits.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeBenefit(index)}
                    className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addBenefit}
              className="text-golden hover:underline text-sm font-semibold"
            >
              + Add Another Benefit
            </button>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Product Image
            </label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImage}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
              />
              {uploadingImage && <span className="text-golden">Uploading...</span>}
            </div>
            {(localPreviewUrl || formData.image_url) && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Current Image:</p>
                <img
                  src={localPreviewUrl || resolveImageUrl(formData.image_url)}
                  alt="Product preview"
                  onError={() => setPreviewError(true)}
                  onLoad={() => setPreviewError(false)}
                  className="w-32 h-32 object-cover rounded-lg border"
                />
                {previewError && (
                  <p className="text-xs text-red-600 mt-2">
                    Preview failed to load. Check image URL or HTTPS/public URL config.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-4 pt-4 border-t">
            <button
              type="submit"
              disabled={loading || uploadingImage}
              className="bg-golden text-white px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : isEditMode ? 'Update Product' : 'Create Product'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProductOperation;
